import { Theme, ThemeConfig } from '../types/theme';
import { predefinedThemes } from '../themes/predefinedThemes';
import { getRemoteEnv } from '../config/remoteEnv';

/** A colour the browser will actually paint: hex, rgb()/rgba(), hsl()/hsla().
 *  Anything else — "var(--text, #fff)" leaking out of a settings UI, the
 *  "#NaNNaNNaN" that deriving from such a value produces, "" — is treated as
 *  absent so the predefined theme's value applies. Biryani Lab's dropdown and
 *  cart went transparent on exactly this (2026-10-06). */
export const isPaintableColor = (v: unknown): v is string =>
    typeof v === 'string' &&
    /^(#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\([^)]*\)|hsla?\([^)]*\))$/i.test(v.trim()) &&
    !/nan/i.test(v);

/** Drop every non-paintable colour from a custom theme (keys are all colours). */
const sanitizeCustomTheme = (custom: Record<string, unknown>): Partial<Theme> => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(custom || {})) if (isPaintableColor(v)) out[k] = v.trim();
    return out as Partial<Theme>;
};

export const loadThemeConfig = async (): Promise<ThemeConfig> => {
    const env = getRemoteEnv();

    const config: ThemeConfig = {};

    if (env.theme) {
        config.predefinedTheme = env.theme as any;
    }

    // theme_custom takes priority over theme_override_accent_color
    if (env.themeCustom) {
        config.customTheme = sanitizeCustomTheme(env.themeCustom as any) as any;
    } else if (isPaintableColor(env.themeOverrideAccentColor)) {

        // if (env.themeOverrideNavbarColor) {
        //     config.customTheme = {
        //         categoryIndicatorColor: env.themeOverrideAccentColor,
        //         loaderColor: env.themeOverrideAccentColor,
        //         sidebarIconColorSelected: env.themeOverrideAccentColor,
        //         cartButtonColor: env.themeOverrideAccentColor,
        //         payButtonColor: env.themeOverrideAccentColor,
        //         topbarBackgroundColor: env.themeOverrideNavbarColor
        //     }
        // }

        // The derivation below parses 6-digit hex only; a navbar colour in any
        // other form (or an unpaintable one) falls back to the chosen theme's
        // own top bar so nothing downstream turns into "#NaNNaNNaN".
        const sixHex = (v: unknown): v is string => typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v.trim());
        const navbarFallback =
            (predefinedThemes as any)[env.theme as any]?.topbarBackgroundColor
            || (env.theme === 'modern-dark' ? '#1a1a1a' : '#ffffff');
        if (env.themeOverrideNavbarColor) {
            const navbarHex = sixHex(env.themeOverrideNavbarColor) ? env.themeOverrideNavbarColor.trim() : navbarFallback;
            const accentHex = sixHex(env.themeOverrideAccentColor) ? env.themeOverrideAccentColor.trim() : '#2563eb';

            // Parse hex to [r, g, b]
            const hexToRgb = (hex: string): [number, number, number] => {
                const h = hex.replace('#', '');
                return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
            };
            const toHex = ([r,g,b]: [number,number,number]) =>
                '#' + [r,g,b].map(v => v.toString(16).padStart(2,'0')).join('');
            const luminance = ([r,g,b]: [number,number,number]) => {
                const lin = (v: number) => { v /= 255; return v <= 0.03928 ? v/12.92 : ((v+0.055)/1.055)**2.4; };
                return 0.2126*lin(r) + 0.7152*lin(g) + 0.0722*lin(b);
            };
            const contrast = (a: [number,number,number], b: [number,number,number]) => {
                const la = luminance(a), lb = luminance(b);
                const [hi, lo] = la > lb ? [la, lb] : [lb, la];
                return (hi + 0.05) / (lo + 0.05);
            };
            const darken = (rgb: [number,number,number], ratio: number): [number,number,number] =>
                rgb.map(v => Math.round(v * ratio)) as [number,number,number];
            const lighten = (rgb: [number,number,number], ratio: number): [number,number,number] =>
                rgb.map(v => Math.min(255, Math.round(v * ratio))) as [number,number,number];


            const toFixedLightness = (rgb: [number,number,number], targetL: number): [number,number,number] => {
                const r = rgb[0]/255, g = rgb[1]/255, b = rgb[2]/255;
                const max = Math.max(r,g,b), min = Math.min(r,g,b), d = max - min;
                let h = 0, s = 0;
                const l = (max + min) / 2;
                if (d !== 0) {
                    s = d / (1 - Math.abs(2*l - 1));
                    if (max === r) h = ((g - b) / d + 6) % 6;
                    else if (max === g) h = (b - r) / d + 2;
                    else h = (r - g) / d + 4;
                    h /= 6;
                }
                // HSL back to RGB with targetL
                const c = (1 - Math.abs(2*targetL - 1)) * s;
                const x = c * (1 - Math.abs((h * 6) % 2 - 1));
                const m = targetL - c/2;
                let r2 = 0, g2 = 0, b2 = 0;
                const hh = h * 6;
                if      (hh < 1) { r2=c; g2=x; }
                else if (hh < 2) { r2=x; g2=c; }
                else if (hh < 3) { g2=c; b2=x; }
                else if (hh < 4) { g2=x; b2=c; }
                else if (hh < 5) { r2=x; b2=c; }
                else             { r2=c; b2=x; }
                return [Math.round((r2+m)*255), Math.round((g2+m)*255), Math.round((b2+m)*255)];
            };
            

            const navbar = hexToRgb(navbarHex);
            const accent = hexToRgb(accentHex);
            const WHITE: [number,number,number] = [255, 255, 255];
            const BLACK: [number,number,number] = [17, 17, 17];

            // iconColor: black on light navbar, white on dark navbar
            const navLum = luminance(navbar);
            const icon = navLum > 0.35 ? BLACK : WHITE;
            const sidebarIconColor = toHex(icon);

            // selectedIconColor: use accent if it contrasts against navbar and differs from icon
            let sidebarIconColorSelected = toHex(accent); // default
            if (contrast(accent, navbar) >= 2.0 && contrast(accent, icon) >= 1.5) {
                sidebarIconColorSelected = toHex(accent);
            } else {
                // Try darkening accent
                let found = false;
                for (let s = 0.9; s >= 0.3; s -= 0.1) {
                    const d = darken(accent, s);
                    if (contrast(d, navbar) >= 2.0 && contrast(d, icon) >= 1.5) {
                        sidebarIconColorSelected = toHex(d); found = true; break;
                    }
                }
                if (!found) {
                    // Try lightening accent
                    for (let s = 1.1; s <= 2.0; s += 0.1) {
                        const l = lighten(accent, s);
                        if (contrast(l, navbar) >= 2.0 && contrast(l, icon) >= 1.5) {
                            sidebarIconColorSelected = toHex(l); found = true; break;
                        }
                    }
                }
                if (!found) {
                    // Last resort: blue tones that work on either navbar tone
                    sidebarIconColorSelected = navLum > 0.35 ? '#2563eb' : '#93c5fd';
                }
            }

            // ── Cart surfaces (Option B): the cart matches the resolved page
            //    background and is separated from the menu by a hairline left
            //    border (that border lives in cart-layout.css). Cards lift a touch
            //    off the page; text follows the page tone. This stays neutral and
            //    consistent across every brand — no risk of a washed tint or a
            //    heavy dark slab, since the brand colour shows in the top bar and
            //    the accent CTA rather than the cart panel itself. ──
            const pageBgHex =
                env.theme === 'modern-light'
                    ? toHex(toFixedLightness(navbar, 0.97))
                    : ((predefinedThemes as any)[env.theme as any]?.backgroundColor
                        || predefinedThemes['modern-light'].backgroundColor);
            const pageBgRgb = hexToRgb(pageBgHex);
            const pageIsDark = Number.isFinite(pageBgRgb[0]) && luminance(pageBgRgb) < 0.35;

            const cartButtonTextColor =
                contrast(accent, WHITE) >= contrast(accent, BLACK) ? toHex(WHITE) : toHex(BLACK);

            const cartTokens = {
                cartBackgroundColor: pageBgHex,
                cartCardColor: toHex(toFixedLightness(navbar, pageIsDark ? 0.20 : 0.99)),
                cartTextColor: pageIsDark ? '#f4f4f5' : toHex(BLACK),
                cartButtonTextColor,
            };

            if (env.theme === 'modern-light') {
                config.customTheme = {
                    ...cartTokens,
                    categoryIndicatorColor: accentHex,
                    loaderColor: accentHex,
                    topbarIconColor: sidebarIconColor,
                    sidebarTextColor: sidebarIconColor,
                    backgroundColor: toHex(toFixedLightness(navbar, 0.97)),
                    cardColor: toHex(toFixedLightness(navbar, 0.99)),
                    
                    sidebarIconColor,
                    sidebarIconColorSelected,
                    topbarBorderBottomColor: navbarHex,
                    sidebarBorderColor: navbarHex,
                    cartButtonColor: accentHex,
                    payButtonColor: accentHex,
                    topbarBackgroundColor: navbarHex,
                    sidebarBackgroundColor: navbarHex
                }
            }
            else{
                config.customTheme = {
                    ...cartTokens,
                    categoryIndicatorColor: accentHex,
                    loaderColor: accentHex,
                    topbarIconColor: sidebarIconColor,
                    sidebarTextColor: sidebarIconColor,
                    sidebarIconColor,
                    sidebarIconColorSelected,
                    topbarBorderBottomColor: navbarHex,
                    sidebarBorderColor: navbarHex,
                    cartButtonColor: accentHex,
                    payButtonColor: accentHex,
                    topbarBackgroundColor: navbarHex,
                    sidebarBackgroundColor: navbarHex
                }
            }
        }
        else {
                config.customTheme = {
                categoryIndicatorColor: env.themeOverrideAccentColor,
                loaderColor: env.themeOverrideAccentColor,
                sidebarIconColorSelected: env.themeOverrideAccentColor,
                cartButtonColor: env.themeOverrideAccentColor,
                payButtonColor: env.themeOverrideAccentColor,
            }
        }

    }
    return config;
};

export const resolveTheme = (config: ThemeConfig): Theme => {
    let baseTheme: Theme;

    if (config.predefinedTheme && predefinedThemes[config.predefinedTheme]) {
        baseTheme = predefinedThemes[config.predefinedTheme];
    } else {
        baseTheme = predefinedThemes['modern-light'];
    }

    if (config.customTheme) {
        return {
            ...baseTheme,
            ...config.customTheme
        };
    }

    return baseTheme;
};